import { Component, Input, AfterViewInit, Output, EventEmitter, OnInit, OnDestroy } from "@angular/core";
import { SignoffService } from "ng/src/app/notes/signoffs/signoff.service";
import { NoteTemplateValueSignoff } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { LogBookUser, LogUser } from "ng/src/app/users/shared/user.model";
import { LogService } from "ng/src/app/logs/log.service";
import { ConfirmationService } from "primeng/api";
import { UserService } from "ng/src/app/users/shared/user.service";
import { forkJoin, Subject } from "rxjs";
import { Utils } from "ng/src/app/core/utils";
import { finalize, map, switchMap, takeUntil } from "rxjs/operators";
import { SignoffControlNote, SignoffTableModel } from "ng/src/app/notes/signoffs/signoff-controls.model";

@Component({
	selector: 'signoff-controls[note]',
	templateUrl: './signoff-controls.component.html',
	styleUrls: ['./signoff-controls.component.scss'],
	providers: [SignoffService]
})
export class SignoffControlsComponent implements OnInit, AfterViewInit, OnDestroy {
	
	@Input('note')
	get note() {
		return this._note;
	}
	set note(val) {
		this._note = val;
		this.refresh(false);
	}
	private _note!: SignoffControlNote;
	
	@Output('refreshed') refreshed = new EventEmitter();
	
	model: SignoffTableModel[] = []
	logUsers: LogUser[] = [];
	
	dialogHeader: string = '';
	dialogVisible = false;
	dialogUsers: { id: number, selected: boolean, disabled: boolean }[] = [];
	
	private refreshSubject = new Subject<boolean>();
	private destroySubject = new Subject();
	private editingUsersOf?: SignoffTableModel;
	private dialogUsersMap: Record<number, { id: number, selected: boolean, disabled: boolean, user: LogUser }> = {};
	
	private get activeUser(): LogBookUser {
		return LogService.selectedLog!.activeLogUser!.user;
	}
	
	constructor(
		private service: SignoffService,
		private userService: UserService,
		private confirmationService: ConfirmationService
	) {}
	
	ngOnInit(): void {
		this.refreshSubject.pipe(
			takeUntil(this.destroySubject),
			switchMap((emitEvent) => {
				return forkJoin({
					model: this.service.getSignoffData(this.note),
					users: this.userService.getLogUsers(LogService.selectedLog!.id)
						.pipe(
							map(res => res.filter(usr => usr.role.id !== 1)),
						)
				})
					.pipe(
						finalize(() => {
							if (emitEvent)
								this.refreshed.next()
						})
					)
			})
		)
			.subscribe(map => {
				this.model = map.model;
				this.logUsers = map.users.sort(Utils.createMultiSortFunction([
					[usr => usr.user.lastName, 'asc'],
					[usr => usr.user.firstName, 'asc']
				]));
			});
	}
	
	ngAfterViewInit(): void {
		this.refresh(false);
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
	}
	
	doSignOff(signoff: SignoffTableModel, type: 'signoff' | 'reject', event: Event) {
		let message = (type === 'signoff') ? signoff.attestation : signoff.refutation;
		let defaultMessage = (type === 'signoff') ? `Sign off on ${signoff.name}` : `Reject sign off on ${signoff.name}`;
		
		this.confirm(event, signoff.segment.id,message ?? defaultMessage,
			type === 'reject',
			() => {
				this.service.doSignoff(signoff.templateValue, this.activeUser, type)
					.subscribe(() => this.refresh());
		})
	}
	
	undoSignoff(model: SignoffTableModel, signoff: NoteTemplateValueSignoff, event: Event) {
		this.confirm(event, signoff.id,
			`Are you sure you want to undo this ${signoff.signoff ? 'signoff' : 'rejection'}
					  by ${signoff.user.firstName} ${signoff.user.lastName}?`,
			false,
			() => {
				this.service.undoSignoff(model.templateValue, signoff)
					.subscribe(() => this.refresh());
			})
	}
	
	refresh(emitEvent: boolean = true) {
		this.refreshSubject.next(emitEvent);
	}
	
	showDialog(model: SignoffTableModel) {
		const userMap = Utils.arrayToMap(model.signoffUsers, usr => usr.id);
		const signoffUsrs = model.signoffs.map(s => s.user);
		
		this.editingUsersOf = model;
		this.dialogHeader = `Setup for ${model.name}`;
		this.dialogUsersMap = this.logUsers.reduce((map, usr) => {
			map[usr.id] = {
				id: usr.id,
				selected: (usr.user.id in userMap),
				disabled: !!signoffUsrs.find(lgUsr => lgUsr.id === usr.user.id),
				user: usr
			};
			return map;
		}, {} as Record<number, { id: number, selected: boolean, disabled: boolean, user: LogUser}>);
		
		this.dialogUsers = Object.values(this.dialogUsersMap)
			.map(usr => { return  { ...usr } })
			.sort(Utils.createMultiSortFunction( [
				[u => u.user.user.lastName, 'asc'],
				[u => u.user.user.firstName, 'asc']
			]));
		this.dialogVisible = true;
	}
	
	updateEditUsers() {
		this.dialogVisible = false;
		if (!this.editingUsersOf) return;
		
		const adding: LogUser[] = [];
		const removing: LogUser[] = [];
		
		this.dialogUsers.forEach(usr => {
			const original = this.dialogUsersMap[usr.id].selected;
			const user = this.dialogUsersMap[usr.id].user;
			
			if (usr.selected === original) return;
			if (usr.selected)
				adding.push(user)
			else
				removing.push(user)
		});
		
		if (adding.length == 0 && removing.length == 0) return;
		this.service.modifySignoffUsers(this.note, this.editingUsersOf.segment, adding, removing)
			.subscribe(() => this.refresh());
	}
	
	private confirm(event: Event, key: string | number, message: string, useDanger: boolean, onAccept: () => void) {
		this.confirmationService.confirm({
			target: event.target ?? undefined,
			key: key.toString(),
			message: message,
			closeOnEscape: true,
			acceptButtonStyleClass: `p-button-${useDanger ? 'danger' : 'primary'}`,
			rejectButtonStyleClass: 'p-button-secondary',
			accept: onAccept
		})
	}
}